import { croodles, identicon } from '@dicebear/collection';
import { createAvatar } from '@dicebear/core';
import { useMutation, useQuery } from '@tanstack/react-query';
import { deletePlayer, editPlayer, getPlayer } from 'api/playerApi';
import { queryClient } from 'App';
import ghostAnimation from 'assets/img/lottie-animation/ghost.json';
import sadAnimation from 'assets/img/lottie-animation/sad-emoji.json';
import ChartPlaceholder from 'components/charts/ChartPlaceholder';
import Flex from 'components/common/Flex';
import SubtleBadge from 'components/common/SubtleBadge';
import LoadingButton from 'components/fantaclasse/LoadingButton';
import { useUser } from 'context/UserContext';
import Lottie from 'lottie-react';
import Loading from 'pages/components/Loading';
import FeedObject from 'pages/feed/FeedObject';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  Image,
  Modal,
  Row
} from 'react-bootstrap';
import {
  BsPersonFillGear,
  BsPersonFillSlash,
  BsPersonPlusFill
} from 'react-icons/bs';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

const TeamListObject = ({ username, name, score }) => {
  const teamLogo = createAvatar(identicon, {
    seed: name,
    size: 100
  }).toDataUriSync();

  return (
    <Row className="justify-content-between mb-3">
      <Col className="d-flex align-items-center">
        <div className="avatar avatar-2xl mx-3">
          <img src={teamLogo} alt="" />
        </div>
        <div>
          <Link to={`/team/${username}`}>
            <h5 className="mb-0 lh-1 text-primary">{name}</h5>
          </Link>
          <h6 className="mb-0 text-700">{username}</h6>
        </div>
      </Col>
      <Col xs={'auto'} className="d-flex align-items-center">
        <h5 className="mb-0 lh-1 fw-semibold">
          <span className="text-800">{score}</span>
          <span className="text-700"> pt</span>
        </h5>
      </Col>
    </Row>
  );
};

TeamListObject.propTypes = {
  username: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  score: PropTypes.number.isRequired
};

const PlayerPagePrefectButton = ({
  text,
  variant,
  icon,
  link,
  setModalShow
}) => {
  // this component comes from Falcon Template: src\components\app\support-desk\quick-links\Admin.js
  return (
    <Col className="text-center px-1">
      <div className="hover-bg-100 rounded-3 position-relative">
        <div
          className={`icon-item icon-item-xl shadow-none mx-auto bg-${variant}-subtle`}
        >
          {icon}
        </div>
        <h6 className="mt-2 mb-0">
          {link !== undefined ? (
            <Link to={link} className="text-900 hover-primary stretched-link">
              {text}
            </Link>
          ) : setModalShow !== undefined ? (
            <Link
              className="text-900 hover-primary stretched-link"
              onClick={() => setModalShow(true)}
            >
              {text}
            </Link>
          ) : (
            text
          )}
        </h6>
      </div>
    </Col>
  );
};

PlayerPagePrefectButton.propTypes = {
  text: PropTypes.string.isRequired,
  variant: PropTypes.string.isRequired,
  icon: PropTypes.element.isRequired,
  link: PropTypes.string,
  setModalShow: PropTypes.func
};

const EditPlayerModal = ({ player, modalShow, setModalShow }) => {
  const [name, setName] = useState(player.name);
  const avatar = createAvatar(croodles, {
    seed: name,
    scale: 120
  }).toDataUriSync();
  const [price, setPrice] = useState(player.price);
  const [validated, setValidated] = useState(false);

  const editPlayerMutation = useMutation({
    mutationFn: editPlayer,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['classroomData'] });
      queryClient.invalidateQueries({ queryKey: [player.code] });
      player.name = name;
      player.price = price;
      toast.success(() => (
        <span>Il giocatore è stato modificato con successo! 🎉</span>
      ));
      setModalShow(false);
    },
    onError: err => {
      toast.error(err.response?.data?.message || err.message);
    }
  });

  const handleSubmit = event => {
    const form = event.currentTarget;
    event.preventDefault();
    if (form.checkValidity() === false) {
      event.stopPropagation();
    } else {
      editPlayerMutation.mutate({
        playerType: player.type,
        playerCode: player.code,
        playerObject: {
          name,
          price
        }
      });
    }
    setValidated(true);
  };

  return (
    <Modal show={modalShow} onHide={() => setModalShow(false)}>
      <Modal.Header closeButton>
        <Modal.Title>
          <h5 className="mb-0">Modifica giocatore</h5>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="p-0 text-center">
          <div className="avatar avatar-4xl">
            <Image roundedCircle src={avatar} />
          </div>
        </div>
        <Form noValidate validated={validated} onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Nome</Form.Label>
            <Form.Control
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              minLength={1}
              maxLength={30}
              required
            />
            <Form.Control.Feedback>Bel nome! 👌🏻</Form.Control.Feedback>
            <Form.Control.Feedback type="invalid">
              Nome non valido.
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Prezzo</Form.Label>
            <Form.Control
              type="number"
              value={price}
              onChange={e => setPrice(e.target.value)}
              min={-500}
              max={500}
              required
            />
            <Form.Control.Feedback type="invalid">
              Il prezzo deve essere compreso tra -500 a 500.
            </Form.Control.Feedback>
          </Form.Group>

          <LoadingButton
            text="Modifica"
            isLoading={editPlayerMutation.isPending}
            disabled={
              (name == player.name && price === player.price) ||
              price < -500 ||
              price > 500
            }
            type="submit"
          />
        </Form>
      </Modal.Body>
    </Modal>
  );
};

EditPlayerModal.propTypes = {
  player: PropTypes.object.isRequired,
  modalShow: PropTypes.bool.isRequired,
  setModalShow: PropTypes.func.isRequired
};

const DeletePlayerModal = ({ player, showModal, setShowModal }) => {
  const navigate = useNavigate();
  const deletePlayerMutation = useMutation({
    mutationFn: deletePlayer,
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ['classroomData'] });
      queryClient.invalidateQueries({ queryKey: [player.code] });
      toast.success(
        <span>
          <strong>{data.name} è stato eliminato! 💔</strong>
        </span>
      );
      navigate('/classe');
    },
    onError: err => {
      toast.error(err.response?.data?.message || err.message);
    }
  });

  const handleDeletePlayer = async () => {
    deletePlayerMutation.mutate({
      playerType: player.type,
      playerCode: player.code
    });
  };

  const [typedName, setTypedName] = useState('');

  return (
    <Modal show={showModal} onHide={() => setShowModal(false)} centered>
      <Modal.Header className="p-4">
        <Modal.Title>
          <h4 className="mb-0">Sei sicuro di voler eliminare {player.name}?</h4>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ul className="mb-0">
          <li className="mb-2">
            Il giocatore <strong>sarà rimosso</strong> e{' '}
            <strong>non potrà più ricevere Bonus o Malus</strong>.
          </li>
          <li className="mb-2">
            Le squadre che hanno acquistato questo giocatore, dovranno giocare
            con{' '}
            <strong>
              {player.type == 'students' ? 'uno studente' : 'un professore'} in
              meno.
            </strong>
          </li>
          <li>
            Se hai bisogno di modificare il nome o il prezzo, puoi modificare il
            giocatore invece di eliminarlo.
          </li>
        </ul>
        <hr />
        <div className="px-3">
          <h4 className="fs-9 py-2">
            Per confermare la tua decisione, scrivi "
            <strong>{player.name}</strong>" qui sotto.
          </h4>
          <Form.Control
            value={typedName}
            onChange={e => setTypedName(e.target.value)}
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowModal(false)}>
          Indietro
        </Button>
        <LoadingButton
          text="Conferma"
          variant="danger"
          isLoading={deletePlayerMutation.isPending}
          disabled={typedName !== player.name}
          action={handleDeletePlayer}
        />
      </Modal.Footer>
    </Modal>
  );
};

DeletePlayerModal.propTypes = {
  player: PropTypes.object.isRequired,
  showModal: PropTypes.bool.isRequired,
  setShowModal: PropTypes.func.isRequired
};

const PlayerPage = ({ playerType }) => {
  const { id } = useParams();
  const user = useUser();

  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const {
    data: playerData,
    isSuccess,
    isError,
    error,
    isLoading
  } = useQuery({
    queryKey: [id],
    queryFn: () => getPlayer(playerType, id)
  });

  const [avatar, setAvatar] = useState('');

  useEffect(() => {
    setAvatar(
      createAvatar(croodles, {
        seed: playerData?.name,
        scale: 120
      }).toDataUriSync()
    );
  }, [playerData]);

  if (isLoading) {
    return <Loading />;
  }

  if (isError) {
    toast.error(error.message);
    return <Navigate to="/classe" />;
  }

  if (isSuccess) {
    playerData.type = playerType;
  }

  return (
    <>
      <EditPlayerModal
        modalShow={showEditModal}
        setModalShow={setShowEditModal}
        player={playerData}
      />
      <DeletePlayerModal
        showModal={showDeleteModal}
        setShowModal={setShowDeleteModal}
        player={playerData}
      />
      <Row className="gx-3">
        <Col className="mb-3">
          <Card className="h-md-100">
            <Card.Body>
              <Flex className="flex-column flex-sm-row align-items-center">
                <div className="p-0 text-center mb-3 mb-sm-0">
                  <div className="avatar avatar-4xl mb-2">
                    <Image roundedCircle src={avatar} className="mb-2" />
                    <SubtleBadge
                      pill
                      bg={playerData.score >= 0 ? 'success' : 'danger'}
                      className="fs-10 position-absolute top-100 translate-middle"
                    >
                      Punti: {playerData.score}
                    </SubtleBadge>
                  </div>
                </div>
                <div className="ms-sm-4 text-center text-sm-start">
                  <h6 className="text-primary mb-1">
                    {playerType == 'students' ? 'Studente' : 'Professore'}
                  </h6>
                  <h3 className="mb-0">{playerData.name}</h3>
                  <p className="mb-0">
                    <strong>Prezzo: </strong>
                    {playerData.price}
                  </p>
                </div>
              </Flex>
            </Card.Body>
          </Card>
        </Col>

        {user.prefect && (
          <Col lg={6} className="mb-3">
            <Card className="h-md-100">
              <Card.Body className="d-flex align-items-center">
                <Container className="p-0">
                  <Row>
                    <PlayerPagePrefectButton
                      text="Modifica"
                      variant="info"
                      icon={<BsPersonFillGear className="text-info fs-6" />}
                      setModalShow={setShowEditModal}
                    />
                    <PlayerPagePrefectButton
                      text="Bonus/Malus"
                      variant="primary"
                      icon={<BsPersonPlusFill className="text-primary fs-6" />}
                      link={`add-points`}
                    />
                    <PlayerPagePrefectButton
                      text="Elimina"
                      variant="danger"
                      icon={<BsPersonFillSlash className="text-danger fs-6" />}
                      setModalShow={setShowDeleteModal}
                    />
                  </Row>
                </Container>
              </Card.Body>
            </Card>
          </Col>
        )}
      </Row>

      <Row className="gx-3">
        <Col lg={6} style={{ height: '300px' }} className="mb-3">
          <Card className="h-100">
            <Card.Header>
              <h5 className="mb-0">Punti</h5>
            </Card.Header>
            <Card.Body>
              <ChartPlaceholder text="Grafico non disponibile" />
            </Card.Body>
          </Card>
        </Col>

        <Col style={{ height: '300px' }} className="mb-3">
          <Card className="h-100">
            <Card.Header>
              <h5 className="mb-0">Squadre</h5>
            </Card.Header>
            <Card.Body className="overflow-auto">
              {playerData.teams.length === 0 && (
                <div className="d-flex justify-content-center align-items-center text-center">
                  <div className="w-75">
                    <Lottie
                      animationData={sadAnimation}
                      loop={true}
                      style={{ height: '100px' }}
                    />
                    <h5>
                      <strong>{playerData.name}</strong> non fa parte di nessuna
                      squadra.
                    </h5>
                  </div>
                </div>
              )}
              {playerData.teams.map(function (team, index) {
                return (
                  <div key={index}>
                    <TeamListObject
                      username={team.username}
                      name={team.name}
                      score={team.score}
                    />
                    <hr />
                  </div>
                );
              })}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="gx-3">
        <Col className="mb-3">
          <Card className="mb-2">
            <Card.Body className="text-center">
              <h5 className="mb-0">Attività recenti</h5>
            </Card.Body>
          </Card>
          {playerData.feed.length === 0 ? (
            <div className="d-flex justify-content-center align-items-center text-center">
              <div className="w-75">
                <Lottie
                  animationData={ghostAnimation}
                  loop={true}
                  style={{ height: '250px' }}
                />
                <h4>Nessun Bonus o Malus ricevuto.</h4>
              </div>
            </div>
          ) : (
            playerData.feed.toReversed().map(function (feedObject, index) {
              return (
                <FeedObject
                  name={feedObject.bonmal.name}
                  points={feedObject.bonmal.points}
                  quantity={feedObject.bonmal.quantity}
                  player={playerData.name}
                  createdAt={feedObject.createdAt}
                  key={index}
                />
              );
            })
          )}
        </Col>
      </Row>
    </>
  );
};

PlayerPage.propTypes = {
  playerType: PropTypes.oneOf(['students', 'teachers'])
};

export default PlayerPage;
